import React, { useState } from "react";
import { api } from "../lib/api";
import { CONTACT, INQUIRY_TYPES } from "../lib/siteData";
import { toast } from "sonner";
import { Mail, MapPin, Linkedin } from "lucide-react";

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    organization: "",
    inquiry_type: INQUIRY_TYPES[0],
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please complete name, email and message.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/site/inquiries", form);
      toast.success("Thank you. Karamjeet will be in touch.");
      setForm({
        name: "",
        email: "",
        organization: "",
        inquiry_type: INQUIRY_TYPES[0],
        message: "",
      });
    } catch (err) {
      toast.error("Could not submit. Please try again or email directly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-20 sm:py-28 bg-ink text-ivory">
      <div className="container-prose grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <p className="overline text-saffron">13 — Connect</p>
          <h2 className="h2-display mt-4 text-4xl sm:text-6xl">
            Bring Karamjeet to your <span className="italic text-saffron">school, district or stage</span>.
          </h2>
          <p className="mt-6 body-prose text-ivory/75 text-base sm:text-lg max-w-md">
            Speaking, teacher training, curriculum design or an exploratory conversation —
            tell Karamjeet a little about your context and he'll respond personally.
          </p>

          <div className="mt-10 space-y-4 text-[15px] body-prose">
            <a href={`mailto:${CONTACT.email}`} data-testid="contact-email" className="flex items-center gap-3 text-ivory/85 hover:text-ivory">
              <Mail size={16} className="text-forest" />
              {CONTACT.email}
            </a>
            <div className="flex items-center gap-3 text-ivory/85">
              <MapPin size={16} className="text-forest" />
              {CONTACT.city}
            </div>
            <a
              href={CONTACT.linkedin}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-linkedin"
              className="flex items-center gap-3 text-ivory/85 hover:text-ivory"
            >
              <Linkedin size={16} className="text-forest" />
              linkedin.com/in/karamsodhi
            </a>
          </div>
        </div>

        <form
          onSubmit={onSubmit}
          data-testid="contact-form"
          className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-6"
        >
          <Field label="Your Name" testid="contact-name">
            <input
              required
              data-testid="contact-name-input"
              value={form.name}
              onChange={onChange("name")}
              className="input-line"
            />
          </Field>
          <Field label="Email" testid="contact-email-field">
            <input
              required
              type="email"
              data-testid="contact-email-input"
              value={form.email}
              onChange={onChange("email")}
              className="input-line"
            />
          </Field>
          <Field label="Organisation" testid="contact-org">
            <input
              data-testid="contact-org-input"
              value={form.organization}
              onChange={onChange("organization")}
              className="input-line"
            />
          </Field>
          <Field label="Inquiry Type" testid="contact-type">
            <select
              data-testid="contact-type-input"
              value={form.inquiry_type}
              onChange={onChange("inquiry_type")}
              className="input-line text-ivory bg-transparent"
            >
              {INQUIRY_TYPES.map((t) => (
                <option key={t} value={t} className="text-ink">{t}</option>
              ))}
            </select>
          </Field>
          <Field label="Message" testid="contact-message" full>
            <textarea
              required
              rows={5}
              data-testid="contact-message-input"
              value={form.message}
              onChange={onChange("message")}
              className="input-line resize-none"
            />
          </Field>
          <div className="sm:col-span-2 mt-2">
            <button
              type="submit"
              disabled={submitting}
              data-testid="contact-submit-button"
              className="inline-flex items-center gap-3 px-7 py-4 bg-forest text-ivory text-[12px] uppercase tracking-[0.22em] font-medium hover:bg-ivory hover:text-ink transition-colors disabled:opacity-60"
            >
              {submitting ? "Sending…" : "Send Inquiry"}
            </button>
          </div>
        </form>
      </div>

      <style>{`
        .input-line {
          width: 100%;
          background: transparent;
          border: 0;
          border-bottom: 1px solid rgba(245,242,234,0.3);
          color: #F5F2EA;
          font-family: 'Outfit', sans-serif;
          font-size: 16px;
          padding: 10px 2px;
          outline: none;
          transition: border-color .25s ease;
        }
        .input-line::placeholder { color: rgba(245,242,234,0.4); }
        .input-line:focus { border-bottom-color: #1B4D3E; }
        .input-line:focus-visible { outline: none; }
      `}</style>
    </section>
  );
}

function Field({ label, children, full, testid }) {
  return (
    <label data-testid={testid} className={`flex flex-col gap-2 ${full ? "sm:col-span-2" : ""}`}>
      <span className="text-[11px] uppercase tracking-[0.22em] text-ivory/60">{label}</span>
      {children}
    </label>
  );
}
